import _ from "lodash";
import { SessionStore } from "../uidispatcherapi/SessionStore";

import FetchArticlesAction from "../actions/FetchArticlesAction";
import * as ArticleService from "../services/ArticleService";
import * as TrackingUtil from "../utils/TrackingUtil";
import * as ArticleCache from "../utils/ArticleCache";
import { Store } from "../utils/Store";

export type LoadingState = "requested" | "loading" | "missing" | "error";
export type ArticleStoreEntry =
    | {
          bundles: {
              [key: string]: any;
          };
      }
    | { state: LoadingState };

let articleMap: {
    [key: string]: ArticleStoreEntry;
} = {};

let isLoading = false;
let stopArticleLoading = false;
let currentStoreId: string | null = null;
let currentLoggedIn = false;
let currentCustomerId: string | null = null;

// FIXME (EK): Do not provide the map in the trigger -> rewrite usages in other stores
class ArticleStore extends Store {
    constructor() {
        super();

        this.listenTo(FetchArticlesAction, this.fetchArticle);
        this.listenTo(SessionStore, this.onSessionStoreTrigger);
        if (!SessionStore.pending()) {
            this.onSessionStoreTrigger();
        }
    }

    onSessionStoreTrigger() {
        const loggedIn = SessionStore.loggedIn();
        const storeId = SessionStore.getCustomerStore();
        const customerId = SessionStore.getCustomerId();
        if (
            (storeId && storeId !== currentStoreId) ||
            customerId !== currentCustomerId ||
            currentLoggedIn !== loggedIn
        ) {
            ArticleCache.clear();
            articleMap = {};
            currentStoreId = storeId;
            currentLoggedIn = loggedIn;
            currentCustomerId = customerId;
        }
    }

    fetchArticle(ids: string[]) {
        this.fetchArticlesWithPreselection(ids, {});
    }

    fetchArticlesWithPreselection(
        ids: string[],
        variantLookupMap: {
            [key: string]: string;
        }
    ) {
        let idsToFetch = [] as string[];
        if (!stopArticleLoading) {
            const idsFromCache = this.handleCachedArticles(ids);
            idsToFetch = _.difference(ids, idsFromCache);
            if (_.isEmpty(idsToFetch)) {
                // all cache hits should trigger the store!
                this.trigger(articleMap);
            }
        }
        if (_.isEmpty(idsToFetch)) {
            stopArticleLoading = false;
            isLoading = false;
            return;
        }

        _.forEach(idsToFetch, id => {
            articleMap[id] = { state: "requested" };
        });

        // load articles in chunks to speed up the first display of articles.
        // Overall load time of a big list might increase, but the users
        // perception will be that the load is faster.
        const chunkSize = 12;
        const chunk = _.take(idsToFetch, chunkSize);
        _.forEach(chunk, id => {
            articleMap[id] = { state: "loading" };
        });
        ArticleService.fetchArticle(chunk, SessionStore.getCustomerStore(), SessionStore.getCustomerId())
            .then(response => {
                this.loadArticleCompleted(response, chunk, variantLookupMap);
                this.fetchArticlesWithPreselection(_.drop(idsToFetch, chunkSize), variantLookupMap);
            })
            .catch(e => this.loadArticlesFailed(e, chunk));
    }

    decideOnTimeToCache(variant: { [x: string]: any }) {
        const bundleWithPriceFail = _.some(variant.bundles, bundle => {
            return _.some(bundle.stores, store => {
                const priceInfo = store && store.sellingPriceInfo;
                return !priceInfo || priceInfo.explanation;
            });
        });
        const bundleWithFlash = _.some(variant.bundles, bundle => {
            return _.some(bundle.stores, store => {
                const priceInfo = store && store.sellingPriceInfo;
                return priceInfo && _.some(priceInfo.dnrInfo, dnrInfo => dnrInfo.flashSalesCounter);
            });
        });
        return bundleWithPriceFail || bundleWithFlash ? 60 : 600;
    }

    loadArticlesFailed(error: any, requestedChunk: string[]) {
        TrackingUtil.trackUIError("ArticleStore", "Fetching articles from evaluate.article failed.", error);
        _.forEach(requestedChunk, id => {
            articleMap[id] = { state: "error" };
        });
        this.trigger(articleMap);
    }

    loadArticleCompleted(
        response: any,
        requestedChunk: string[],
        variantLookupMap: {
            [key: string]: string;
        }
    ) {
        const articles = response.result;
        const resultVariants = {} as { [variantId: string]: any };
        _.forEach(articles, article => {
            _.forEach(article.variants, variant => {
                const bettyVariantId = variant.variantId.bettyVariantId;
                const fetchedId = variantLookupMap[bettyVariantId];
                if (fetchedId) {
                    resultVariants[fetchedId] = variant;
                } else {
                    resultVariants[bettyVariantId] = variant;
                }
            });
        });
        _.forEach(requestedChunk, id => {
            if (resultVariants[id]) {
                articleMap[id] = resultVariants[id];
                ArticleCache.put(id, resultVariants[id], this.decideOnTimeToCache(resultVariants[id]));
            } else {
                articleMap[id] = { state: "missing" };
            }
        });
        this.trigger(articleMap);
    }

    stopArticleLoading() {
        if (isLoading) {
            stopArticleLoading = true;
        }
    }

    handleCachedArticles(ids: string[]) {
        const cachedData = ArticleCache.getAll(ids);
        if (!_.isEmpty(cachedData.ids)) {
            _.forEach(cachedData.ids, id => {
                articleMap[id] = cachedData.data[id];
            });
        }
        return cachedData.ids;
    }

    getArticleData(ids: string[]) {
        return ArticleCache.getAll(ids);
    }

    getOneArticleData(id: string): ArticleStoreEntry | null | undefined {
        return articleMap[id];
    }

    // for testing
    _reset() {
        articleMap = {};
    }

    _getArticleMap() {
        return articleMap;
    }

    _getCurrentStoreId() {
        return currentStoreId;
    }

    _getCurrentCustomerId() {
        return currentCustomerId;
    }

    _getCurrentLoggedIn() {
        return currentLoggedIn;
    }
}

export default new ArticleStore();
